import {
  Breadcrumbs,
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  Modal,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  AlertTitle,
  Link as MuiLink,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useNavigate } from "react-router-dom";

import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getDsokMenu, updateDsokMenu } from "app/services/api/menu";
import { getDSOKCategories, getCustomer } from "app/services/api/management";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import Div from "@jumbo/shared/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { AddBox, DragIndicator } from "@mui/icons-material";
import DSOKMenuCategories from "./MenuCategories";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const DsokEditMenu = () => {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const { getAuthUser } = useJumboAuth();
  const authUser = getAuthUser();
  const customerUrl = authUser?.customer?.url || "";
  const { t, i18n } = useTranslation();

  const [updating, setUpdating] = useState(false);
  const [menu, setMenu] = useState();
  const [openModal, setOpenModal] = useState();
  // Category display names looked up by id from the categories collection.
  // The menu may store categories without a (current) name, so we resolve the
  // real name here for display (always in the customer's default language) and
  // fall back to the stored name / id.
  const [categoryNamesById, setCategoryNamesById] = useState({});
  const [customerDefaultLanguage, setCustomerDefaultLanguage] = useState(null);
  const formRef = useRef();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "75vw",
    maxHeight: "70vh",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    overflow: "auto",
  };

  const Schema = Yup.object().shape({
    code: Yup.string().required(t("sidebar.menuItem.required")),
    isActive: Yup.boolean().required(t("sidebar.menuItem.required")),
  });

  useEffect(() => {
    const loadMenu = async () => {
      try {
        const response = await getDsokMenu(menuId);
        // Normalize title to a simple string and ensure categories is an object
        const normalizedTitle = (() => {
          const titleVal = response?.title;
          if (typeof titleVal === "object" && titleVal !== null) {
            const values = Object.values(titleVal);
            return (values && values[0]) || "";
          }
          return String(titleVal || "");
        })();
        const menuWithCategories = {
          ...response,
          title: normalizedTitle,
          categories: response.categories || {},
        };
        setMenu(menuWithCategories);
      } catch (error) {
        console.error("Error loading menu:", error);
      }
    };

    loadMenu();
  }, [menuId]);

  // Resolve a category name object (e.g. { "sk-SK": "Nápoje" }) to a display string,
  // preferring the customer's configured default language.
  const resolveCategoryName = (name) => {
    if (name && typeof name === "object") {
      const lang = customerDefaultLanguage || i18n?.language;
      return (lang && name[lang]) || Object.values(name).find((v) => v) || "";
    }
    return typeof name === "string" ? name : "";
  };

  // Load the customer's default language, then the full categories collection,
  // to resolve category display names by id (menu keys categories by category _id).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        let lang = null;
        if (customerUrl) {
          const customer = await getCustomer(customerUrl);
          lang = customer?.dsokSettings?.customer?.defaultLanguage || null;
          if (!cancelled && lang) setCustomerDefaultLanguage(lang);
        }
        const cats = await getDSOKCategories();
        if (cancelled || !cats || typeof cats !== "object") return;
        const uiLang = i18n?.language;
        const map = {};
        Object.keys(cats).forEach((objKey) => {
          const cat = cats[objKey];
          if (!cat || typeof cat !== "object") return;
          const name = cat.name;
          let resolved = "";
          if (name && typeof name === "object") {
            resolved =
              (lang && name[lang]) ||
              (uiLang && name[uiLang]) ||
              Object.values(name).find((v) => v) ||
              "";
          } else if (typeof name === "string") {
            resolved = name;
          }
          if (!resolved) return;
          // Key by the category _id (menu categories are keyed by _id); also index
          // by the raw object key as a fallback in case the API keys differ.
          const idKey = cat._id != null ? String(cat._id) : null;
          if (idKey) map[idKey] = String(resolved);
          map[String(objKey)] = String(resolved);
        });
        if (!cancelled) setCategoryNamesById(map);
      } catch (e) {
        // non-fatal: fall back to the stored name / id
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerUrl, i18n?.language]);

  const handleUpdateMenu = async (menu) => {
    // Preserve the order from the UI state by using Object.keys() which maintains the drag & drop order
    const orderedCategoryKeys = Object.keys(menu.categories);
    const filteredCategories = orderedCategoryKeys.reduce((acc, key) => {
      const category = menu.categories[key];
      if (category && typeof category === "object") {
        acc[key] = {
          _id: category._id,
          name: category.name,
          isActive: category.isActive !== false,
          // Add explicit order field if needed by API
          order: orderedCategoryKeys.indexOf(key),
        };
      }
      return acc;
    }, {});
    menu.categories = filteredCategories;
    setUpdating(true);
    try {
      await updateDsokMenu(menu);
      navigate("/customer/dsok/menu");
    } catch (error) {
      console.log(error);
    }
    setUpdating(false);
  };

  const SortableCategoryItem = ({
    category,
    categoryId,
    onRemove,
    onToggleActive,
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: categoryId });

    // If category doesn't exist, don't render the item
    if (!category) {
      return null;
    }

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const handleRemove = (e) => {
      e.stopPropagation();
      onRemove(categoryId);
    };

    return (
      <Box
        ref={setNodeRef}
        style={style}
        sx={{
          mb: 1,
          p: 1,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "grab",
          "&:active": {
            cursor: "grabbing",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
          }}
        >
          <Box {...attributes} {...listeners}>
            <DragIndicator
              sx={{ color: "text.secondary", fontSize: 20, cursor: "grab" }}
            />
          </Box>
          <Typography>
            <MuiLink
              component="button"
              variant="body1"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation(); // Prevent drag/sort interference
                if (categoryId) {
                  // Open category detail in new tab to preserve menu context
                  window.open(
                    `/customer/dsok/categories/${categoryId}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }
              }}
              sx={{
                cursor: categoryId ? "pointer" : "default",
                textDecoration: "none",
                color: "primary.main",
                "&:hover": {
                  textDecoration: categoryId ? "underline" : "none",
                },
              }}
            >
              {(() => {
                const resolved =
                  categoryNamesById[categoryId] ||
                  resolveCategoryName(category.name);
                return resolved || `Category ${categoryId}`;
              })()}
            </MuiLink>
            {categoryId && (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, fontSize: "0.75rem" }}
              >
                (
                <MuiLink
                  component="button"
                  variant="caption"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation(); // Prevent drag/sort interference
                    if (categoryId) {
                      // Open category detail in new tab to preserve menu context
                      window.open(
                        `/customer/dsok/categories/${categoryId}`,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }
                  }}
                  sx={{
                    cursor: categoryId ? "pointer" : "default",
                    textDecoration: "none",
                    color: "text.secondary",
                    "&:hover": {
                      textDecoration: categoryId ? "underline" : "none",
                      color: "primary.main",
                    },
                  }}
                >
                  {categoryId}
                </MuiLink>
                )
              </Typography>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={t("pos.status", { defaultValue: "Status" })}>
            <Switch
              size="small"
              checked={category?.isActive !== false}
              onChange={(e) => onToggleActive?.(categoryId, e.target.checked)}
              inputProps={{ "aria-label": "toggle category active" }}
            />
          </Tooltip>
          <IconButton size="small" onClick={handleRemove}>
            <DeleteForeverIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/menu">{t("sidebar.menuItem.dsokMenus")}</Link>
        <Typography color="text.primary">{menu?.title}</Typography>
      </Breadcrumbs>
      <Div
        sx={{
          display: "flex",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: 1,
            boxShadow: 1,
            p: 3,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={12}>
              <Stack
                direction={"row"}
                spacing={2}
                display="flex"
                sx={{ mb: 2, width: "100%" }}
                alignItems="center"
              >
                {menu && (
                  <Formik
                    validateOnChange={true}
                    innerRef={formRef}
                    initialValues={menu}
                    validationSchema={Schema}
                    onSubmit={async (data) => {
                      await handleUpdateMenu(data);
                    }}
                  >
                    {(props) => (
                      <Form
                        style={{ textAlign: "left", width: "100%" }}
                        noValidate
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6} lg={2}>
                            <FormControlLabel
                              control={
                                <Field
                                  name={"isActive"}
                                  component={Switch}
                                  onChange={(e) =>
                                    props.setFieldValue(
                                      "isActive",
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label={t("pages.products.active")}
                              checked={props.values.isActive}
                            />
                          </Grid>
                          <Grid item xs={12} md={4} lg={4}>
                            <Div sx={{ mt: 0, mb: 1, mx: 0, flex: 1 }}>
                              <JumboTextField
                                fullWidth
                                required
                                name={`code`}
                                disabled={true}
                                label={t("sidebar.menuItem.code")}
                              />
                            </Div>
                          </Grid>

                          <Grid item xs={12} md={6} lg={6}>
                            <Div sx={{ mt: 0, mb: 1, mx: 0 }}>
                              <JumboTextField
                                fullWidth
                                name={`title`}
                                disabled={true}
                                label={t("sidebar.menuItem.title")}
                              />
                            </Div>
                          </Grid>
                        </Grid>
                        <Divider sx={{ my: 2 }} />
                        <Grid item xs={12} md={6} lg={6} sx={{ mt: 2 }}>
                          <Div sx={{ mt: 0, mb: 1, mx: 0 }}>
                            <Stack
                              direction="row"
                              display="flex"
                              alignItems="center"
                              spacing={1}
                              sx={{ mb: 2 }}
                            >
                              <Typography variant="h4">
                                {t("sidebar.menuItem.categories")}
                              </Typography>
                              <Tooltip
                                title={t("pages.articleCategories.addCategory")}
                              >
                                <IconButton
                                  onClick={() =>
                                    setOpenModal({ type: "addCategory" })
                                  }
                                >
                                  <AddBox />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                            <Field
                              component={({ field, form }) => (
                                <Box>
                                  {field.value &&
                                  typeof field.value === "object" &&
                                  Object.keys(field.value).length > 0 ? (
                                    <DndContext
                                      sensors={sensors}
                                      collisionDetection={closestCenter}
                                      onDragEnd={(event) => {
                                        const { active, over } = event;
                                        if (active.id !== over.id) {
                                          const categoryKeys = Object.keys(
                                            field.value
                                          );
                                          const oldIndex = categoryKeys.indexOf(
                                            active.id
                                          );
                                          const newIndex = categoryKeys.indexOf(
                                            over.id
                                          );

                                          // Use arrayMove to reorder the keys while preserving the drag & drop order
                                          const reorderedKeys = arrayMove(
                                            categoryKeys,
                                            oldIndex,
                                            newIndex
                                          );

                                          // Reconstruct the categories object in the new order
                                          const newCategories = {};
                                          reorderedKeys.forEach((key) => {
                                            newCategories[key] =
                                              field.value[key];
                                          });

                                          form.setFieldValue(
                                            "categories",
                                            newCategories
                                          );
                                        }
                                      }}
                                    >
                                      <SortableContext
                                        items={Object.keys(field.value)}
                                        strategy={verticalListSortingStrategy}
                                      >
                                        {Object.keys(field.value)
                                          .map((key) => {
                                            const category = field.value[key];
                                            if (
                                              !category ||
                                              typeof category !== "object"
                                            )
                                              return null;

                                            return (
                                              <SortableCategoryItem
                                                key={key}
                                                categoryId={key}
                                                category={category}
                                                onToggleActive={(
                                                  categoryId,
                                                  checked
                                                ) => {
                                                  const updated = {
                                                    ...field.value,
                                                    [categoryId]: {
                                                      ...field.value[
                                                        categoryId
                                                      ],
                                                      isActive: checked,
                                                    },
                                                  };
                                                  form.setFieldValue(
                                                    "categories",
                                                    updated
                                                  );
                                                }}
                                                onRemove={(categoryId) => {
                                                  const newCategories = {
                                                    ...field.value,
                                                  };
                                                  delete newCategories[
                                                    categoryId
                                                  ];
                                                  form.setFieldValue(
                                                    "categories",
                                                    newCategories
                                                  );
                                                }}
                                              />
                                            );
                                          })
                                          .filter(Boolean)}
                                      </SortableContext>
                                    </DndContext>
                                  ) : (
                                    <Alert severity="info">
                                      <AlertTitle>
                                        {t("sidebar.menuItem.noCategories")}
                                      </AlertTitle>
                                    </Alert>
                                  )}
                                </Box>
                              )}
                              name="categories"
                            />
                          </Div>
                        </Grid>
                        <Div
                          sx={{
                            mt: 2,
                            mb: 1,
                            mx: 2,
                            gap: "4px",
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            size="normal"
                            onClick={() => navigate("/customer/dsok/menu")}
                          >
                            {t("buttons.cancel")}
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            size="normal"
                            disabled={updating}
                          >
                            {updating
                              ? t("buttons.updating")
                              : t("buttons.save")}
                          </Button>
                        </Div>
                      </Form>
                    )}
                  </Formik>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Div>
      <Modal open={openModal !== undefined} onClose={() => setOpenModal()}>
        <Div sx={modalStyle}>
          {openModal?.type === "addCategory" && (
            <>
              <DSOKMenuCategories
                setFieldValue={(field, value) => {
                  // Update the main form directly
                  if (formRef.current && formRef.current.setFieldValue) {
                    if (field === "categories") {
                      // Get current categories and merge with new ones
                      const currentCategories =
                        formRef.current.getFieldProps("categories").value || {};
                      formRef.current.setFieldValue(field, {
                        ...currentCategories,
                        ...value,
                      });
                    } else {
                      formRef.current.setFieldValue(field, value);
                    }
                  }
                }}
                values={menu || {}}
                setCloseModal={() => setOpenModal()}
              />
            </>
          )}
        </Div>
      </Modal>
    </>
  );
};

export default DsokEditMenu;
